import React, { useMemo, useState } from 'react';

import { Chip, Link, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { type PromoFeatureKey } from '@luxuryescapes/contract-svc-promo';

import DebugModal from '~/components/DebugModal/DebugModal';

import useCurrentUser from '~/hooks/useCurrentUser';
import { usePromoFeaturesConfig } from '~/hooks/usePromoFeatureAvailability';
import { usePromoPreferences } from '~/hooks/usePromoPreferences';
import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { promoRoles } from '~/services/PromoService';

import PromoFeatureRoleAccess from './PromoFeatureRoleAccess';
import {
  type PromoFeatureIndicator,
  getPromoFeatureIndicators,
  getPromoFeatureRolloutGroup,
  promoFeatureRolloutGroups,
} from './promoFeatureIndicators';

interface Props {
  featureUrls: Readonly<Partial<Record<PromoFeatureKey, string>>>;
}

function matchesSearch(feature: PromoFeatureIndicator, searchText: string) {
  const normalizedSearchText = searchText.trim().toLowerCase();

  if (!normalizedSearchText) {
    return true;
  }

  return [
    feature.key,
    feature.displayName,
    feature.description,
    feature.experimentalStatus,
    feature.currentStatusHeadline,
    feature.isFunctionallyComplete ? 'functionally complete' : '',
    feature.isPublic ? 'public' : 'private',
    ...(feature.promoBrands ?? []),
    ...(feature.regions ?? []),
  ].some((value) => value.toLowerCase().includes(normalizedSearchText));
}

export default function PromoFeatures({ featureUrls }: Props) {
  const { user } = useCurrentUser();
  const { hasRole, hasLoaded } = useUserPromoRoles({
    userId: user?.id_member ?? '',
    enabled: Boolean(user?.id_member),
  });
  const [promoPreferences] = usePromoPreferences();
  const voucherUserPreferences = promoPreferences.uiSelection.voucherUsers;
  const [searchText, setSearchText] = useState('');
  const { config, optedInFeatureKeys, setOptedIn } = usePromoFeaturesConfig();
  const experimentalFeatures = config.features;
  const isPromoBetaTester = hasLoaded && hasRole(promoRoles.PROMO_BETA_TESTER_ROLE);
  const featureIndicators = useMemo(() => getPromoFeatureIndicators(experimentalFeatures), [experimentalFeatures]);
  const features = useMemo(
    () => featureIndicators.filter((feature) => matchesSearch(feature, searchText)),
    [featureIndicators, searchText],
  );
  const columns = useMemo<Array<GridColDef<PromoFeatureIndicator>>>(
    () => [
      {
        field: 'displayName',
        headerName: 'Feature',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Stack sx={{ pl: params.row.depth * 2 }}>
            <Typography variant="body2">{params.row.displayName}</Typography>
            {params.row.parentKeys.length > 0 && (
              <Typography color="text.secondary" variant="caption">
                Part of {params.row.parentKeys.join(' / ')}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 280,
      },
      {
        field: 'currentStatusHeadline',
        headerName: 'Current rollout',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'canUse',
        headerName: 'Access requirements',
        minWidth: 260,
        flex: 1,
        renderCell: (params) => (
          <PromoFeatureRoleAccess
            feature={params.row}
            config={config}
            canToggleOptIn={isPromoBetaTester}
            optedInFeatureKeys={optedInFeatureKeys}
            onSetOptedIn={setOptedIn}
          />
        ),
      },
      {
        field: 'availability',
        headerName: 'Availability',
        minWidth: 220,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.5}>
            <Typography variant="caption">{row.isPublic ? 'Public feature' : 'Private feature'}</Typography>
            <Typography variant="caption">
              Brands: {row.promoBrands ? row.promoBrands.join(', ') || 'None' : 'All'}
            </Typography>
            <Typography variant="caption">Regions: {row.regions ? row.regions.join(', ') || 'None' : 'All'}</Typography>
          </Stack>
        ),
      },
      {
        field: 'isOnlyTest',
        headerName: 'Test only',
        minWidth: 110,
        renderCell: (params) =>
          params.row.isOnlyTest ? <Chip label="Test only" size="small" color="warning" /> : null,
      },
      {
        field: 'isFunctionallyComplete',
        headerName: 'Complete',
        minWidth: 120,
        renderCell: (params) =>
          params.row.isFunctionallyComplete ? <Chip label="Complete" size="small" color="success" /> : null,
      },
      {
        field: 'openFeature',
        headerName: 'Open',
        sortable: false,
        filterable: false,
        minWidth: 120,
        renderCell: (params) => {
          const featureUrl = featureUrls[params.row.key];

          return featureUrl ? <Link href={featureUrl}>Open feature</Link> : null;
        },
      },
    ],
    [config, featureUrls, isPromoBetaTester, optedInFeatureKeys, setOptedIn],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Promo service features and their rollout status. Promo beta testers can enable explicitly opt-in capabilities
        for this browser session.
      </Typography>

      <TextField
        label="Search promo features"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        fullWidth
      />

      {promoFeatureRolloutGroups.map((group) => {
        const rows = features.filter((feature) => getPromoFeatureRolloutGroup(feature) === group);

        if (rows.length === 0) {
          return null;
        }

        return (
          <Stack key={group} spacing={1}>
            <Typography component="h3" variant="h6">
              {group}
            </Typography>
            <DataGrid
              aria-label={`Promo service features: ${group}`}
              columns={columns}
              rows={rows}
              getRowId={(feature) => feature.key}
              getRowHeight={() => 'auto'}
              disableColumnMenu
              disableRowSelectionOnClick
              hideFooter
              autoHeight
              sx={{ '.MuiDataGrid-cell': { alignItems: 'center', py: 1 } }}
            />
          </Stack>
        );
      })}

      <DebugModal
        title="Feature debug"
        type="generic"
        data={{ voucherUserPreferences, promoPreferences, features: experimentalFeatures }}
        buttonProps={{ size: 'small', variant: 'text' }}
      />
    </Stack>
  );
}
